import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Dialog, IconButton, makeStyles, Typography } from '@material-ui/core';
import { CloseIcon } from '../Icon';
import { i18n } from '../../language';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      width: '80%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      background: 'transparent',
      boxShadow: 'unset',
    },
  },
  root: {
    width: '100%',
    maxWidth: '100rem',
    display: 'flex',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    background: '#fff',
    height: '100%',
    overflow: 'auto',
    minWidth: '100%',
    flexDirection: 'column',
    padding: '2.375rem 1.53rem 1.875rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    alignItems: 'center',
    marginBottom: '1.563rem',
  },
  headerTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  closeIcon: {
    fontSize: '1.375rem',
    color: '#538fff',
  },
  main: {
    flex: 1,
    overflow: 'auto',
  },
  imageContainer: ({ scaleVal }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    cursor: scaleVal === 1.5 ? 'grab' : 'pointer',
    overflow: scaleVal === 1.5 ? 'auto' : 'hidden',
  }),
  scaleText: {
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: 'white',
    background: '#4c93ff',
    padding: '0.2rem',
    width: '5rem',
    textAlign: 'center',
    borderRadius: '2rem',
  },
  previewImg: ({ scaleVal }) => ({
    width: scaleVal === 1.5 ? '150%' : '100%',
    height: scaleVal === 1.5 ? '150%' : '100%',
    objectFit: 'contain',
    margin: 'auto',
    flexShrink: 0,
    transformOrigin: 'center center',
    pointerEvents: 'none',
    userSelect: 'none',
  }),
}));

export default function PreviewDialog({ showDialog, closePop, previewUrl }) {
  const [scaleVal, setScaleVal] = useState(1);
  const classes = useStyles({ scaleVal });
  const containerRef = useRef(null);
  // 使用 useRef 存储拖拽信息，避免作为依赖项导致页面一直刷新
  const isDraggingRef = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // 鼠标按下记录位置
  const handleMouseDown = useCallback(
    (e) => {
      if (!containerRef.current) return;

      // 始终记录起始位置，用于判断是点击还是拖拽
      const position = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: containerRef.current.scrollLeft,
        scrollTop: containerRef.current.scrollTop,
      };
      startPositionRef.current = position;

      // 只有在 1.5 倍时才开启拖拽标记
      if (scaleVal === 1.5) {
        isDraggingRef.current = true;
      }
    },
    [scaleVal]
  );

  // 鼠标移动时拖拽
  const handleMouseMove = useCallback(
    (e) => {
      if (scaleVal === 1.5 && containerRef.current && isDraggingRef.current) {
        // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
        e.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = e.clientX - startPos.x;
        const deltaY = e.clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        containerRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [scaleVal]
  );

  // 鼠标抬起
  const handleMouseUp = useCallback(
    (e) => {
      const startPos = startPositionRef.current;
      const deltaX = Math.abs(e.clientX - startPos.x);
      const deltaY = Math.abs(e.clientY - startPos.y);

      // 移动距离小于 5px 视为点击
      const isClick = deltaX < 3 && deltaY < 3;
      if (isClick) {
        // 点击逻辑：如果是 1 倍则变 1.5，如果是 1.5 则变 1
        setScaleVal((prev) => (prev === 1 ? 1.5 : 1));
      }
      isDraggingRef.current = false;
    },
    [scaleVal]
  );

  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // 当scaleVal变化时，自动滚动到中间位置
  useEffect(() => {
    if (scaleVal === 1.5 && containerRef.current) {
      // 计算滚动到中间的位置
      const container = containerRef.current;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const maxScrollTop = container.scrollHeight - container.clientHeight;

      // 滚动到中间位置
      container.scrollLeft = maxScrollLeft / 2;
      container.scrollTop = maxScrollTop / 2;
    } else if (scaleVal === 1 && containerRef.current) {
      // 恢复到原始大小时，重置滚动位置
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  }, [scaleVal]);

  const handleTouchStart = useCallback((e) => {
    if (!containerRef.current) return;

    // 始终记录起始位置，用于判断是点击还是拖拽
    const position = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    };
    startPositionRef.current = position;

    // 只有在 1.5 倍时才开启拖拽标记
    if (scaleVal === 1.5) {
      isDraggingRef.current = true;
    }
  }, []);

  const handleTouchMove = useCallback(
    (event) => {
      if (scaleVal === 1.5 && containerRef.current && isDraggingRef.current) {
        // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
        event.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = event.changedTouches[0].clientX - startPos.x;
        const deltaY = event.changedTouches[0].clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        containerRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [scaleVal]
  );

  return (
    <Dialog open={showDialog} maxWidth={false} className={classes.dialog}>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{i18n.t('EHANDOVER_PREVIEW')}</Typography>
          <IconButton id="select_ward_close_btn" className={classes.closeIcon} onClick={closePop}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
        <main className={classes.main}>
          <div
            ref={containerRef}
            className={classes.imageContainer}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
            onTouchCancel={handleMouseLeave}
          >
            <img src={previewUrl} alt="preview img" className={classes.previewImg} />
          </div>
        </main>
        <Box display="flex" justifyContent="center" style={{ margin: '1.25rem 0 1.875rem 0' }}>
          <Box gridGap="2rem" flexDirection="row" display="flex">
            <Typography className={classes.scaleText} onClick={() => setScaleVal(1)}>
              1x
            </Typography>
            <Typography className={classes.scaleText} onClick={() => setScaleVal(1.5)}>
              1.5x
            </Typography>
          </Box>
        </Box>
      </div>
    </Dialog>
  );
}
